import type { FunctionComponent } from 'preact';
import { useMemo } from 'preact/hooks';

import type { TemplateFunction, TemplateResult } from 'Components/types';
import { removalFromGarageRequested, vehicleSelected } from 'Core/actions/fitmentSearch';
import {
  createVehicleIsSelectedSelector,
  fitmentSearchBaseFieldsSelector,
  fitmentSearchExtraFieldsSelector,
  selectedVehicleSelector,
} from 'Core/selectors/fitmentSearch';
import { useDispatch, useSelector } from 'Core/store';
import type { Vehicle } from 'Models';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';

import type { VehicleListItemParams } from '../types';

type Props = {
  close?: () => void;
  doNotRedirectOnVehicleSelect?: boolean;
  redirectUrl?: string;
  template: TemplateFunction<VehicleListItemParams>;
  vehicle: Vehicle;
};

const VehicleListItem: FunctionComponent<Props> = ({
  close,
  doNotRedirectOnVehicleSelect,
  redirectUrl,
  template,
  vehicle,
}): TemplateResult => {
  const dispatch = useDispatch();

  const isSelectedSelector = useMemo(() => createVehicleIsSelectedSelector(vehicle), [vehicle]);
  const isSelected = useSelector(isSelectedSelector);
  const selectedVehicle = useSelector(selectedVehicleSelector);
  const baseFitmentFields = useSelector(fitmentSearchBaseFieldsSelector);
  const extraFitmentFields = useSelector(fitmentSearchExtraFieldsSelector);

  const selectVehicle = () => {
    dispatch(
      vehicleSelected(vehicle, {
        doNotRedirectOnVehicleSelect,
        isSelectedFromGarage: true,
        redirectUrl,
        sendIsolatedRequest: true,
      }),
    );
    fitmentSearchConfig.onVehicleSelected(vehicle.filteredFieldMap);
    close?.();
  };

  const removeVehicle = (e?: Event) => {
    e?.stopPropagation();
    dispatch(removalFromGarageRequested(vehicle, vehicle.equalsBySelection(selectedVehicle)));
    fitmentSearchConfig.onVehicleDiscarded();
  };

  return template.call({
    vehicle: vehicle.filteredFieldMap,
    vehicleString: vehicle.toString(),
    vehicleBaseValues: vehicle.getFilteredValues(baseFitmentFields),
    vehicleExtraValues: vehicle.getFilteredValues(extraFitmentFields),
    vehicleLogo: vehicle.logo,
    isSelected,
    selectVehicle,
    removeVehicle,
  });
};

export default VehicleListItem;
